/**
 * BẢNG SO SÁNH DNS KHI NHẬP LẠI TÊN MIỀN ĐÃ TỪNG CÓ — OQ-128 việc 7 (OQ-133).
 *
 * Khi một kết nối mới gắn vào tên miền đã có trong hệ thống, bản ghi DNS ở CSDL
 * và trên Cloudflare có thể lệch nhau. Màn này hiện bốn trạng thái so sánh và cho
 * người dùng chọn giữ phía nào cho từng bản ghi, theo hai pha:
 *
 *   Pha 1 — XEM TRƯỚC (chỉ đọc): GET /domains/import/compare.
 *   Pha 2 — ÁP (ghi): POST /domains/import/apply với đúng tập lựa chọn.
 *
 * Bất biến an toàn (chủ dự án chốt): bước áp KHÔNG BAO GIỜ xoá bản ghi trên
 * Cloudflare — chỉ TẠO và CẬP NHẬT. "Không giữ" chỉ bỏ khỏi CSDL. Server cưỡng
 * chế điều này (chốt chặn oq-133-no-cf-delete) và kiểm lệch giữa hai pha; nếu bản
 * ghi đã đổi trên Cloudflare kể từ lúc xem trước thì báo 422 để so sánh lại thay
 * vì ghi đè âm thầm.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError, thanhApiError, useLoiApi } from '../lib/api';
import { useT } from '../lib/i18n';
import { Modal } from '../components/Modal';
import { ApiErrorAlert } from '../components/ApiErrorAlert';
import { AsyncSection, EmptyState, Skeleton } from '../components/States';
import { useToast } from '../components/Toast';

type DiffState = 'same' | 'db_only' | 'cf_only' | 'differ';
type Decision = 'keep' | 'drop' | 'use_cf' | 'use_db';

interface ComparedSide {
  cfRecordId: string | null;
  content: string;
  ttl: number;
  proxied: boolean;
  priority: number | null;
}

interface ComparedRecord {
  key: string;
  type: string;
  name: string;
  displayName: string;
  state: DiffState;
  db: ComparedSide | null;
  cf: ComparedSide | null;
}

interface ReimportComparison {
  domainId: string;
  name: string;
  records: ComparedRecord[];
  summary: { same: number; dbOnly: number; cfOnly: number; differ: number };
}

interface ApplyResult {
  domainId: string;
  createdOnCf: number;
  updatedOnCf: number;
  updatedFromCf: number;
  importedToDb: number;
  droppedFromDb: number;
  kept: number;
  skipped: number;
}

/*
 * Lựa chọn mặc định AN TOÀN cho từng trạng thái:
 * - same     → keep   (không làm gì)
 * - differ   → use_cf (lấy phía Cloudflare — không đổi bản ghi đang phục vụ)
 * - db_only  → keep   (tạo trên CF; phần thiếu được bù)
 * - cf_only  → keep   (nhập về CSDL)
 * Không trạng thái nào mặc định xoá/ghi đè phía đang phục vụ traffic.
 */
const MAC_DINH: Record<DiffState, Decision> = {
  same: 'keep',
  differ: 'use_cf',
  db_only: 'keep',
  cf_only: 'keep',
};

const STATE_CHIP: Record<DiffState, string> = {
  same: 'chip ok',
  differ: 'chip warn',
  db_only: 'chip info',
  cf_only: 'chip accent',
};

/* Nhận 	 từ ngoài: đây là hàm thuần dựng chuỗi tóm tắt, không phải component,
   nên không treo hook được — nhưng chuỗi nó dựng vẫn hiện cho người dùng đọc. */
function fmtSide(
  side: ComparedSide | null,
  t: (k: string, v?: Record<string, string | number>) => string,
): string {
  if (!side) return '—';
  const bits = [side.content];
  if (side.priority !== null) bits.push(t('dns.priorityShort', { n: side.priority }));
  bits.push(`TTL ${side.ttl}`);
  if (side.proxied) bits.push('proxied');
  return bits.join(' · ');
}

export function ReimportCompareModal({
  connectionId,
  zoneId,
  zoneName,
  onClose,
  onDone,
}: {
  connectionId: string;
  zoneId: string;
  zoneName: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const t = useT();
  const { toast } = useToast();
  const [chon, setChon] = useState<Record<string, Decision>>({});
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const compareQuery = useQuery({
    queryKey: ['reimport-compare', connectionId, zoneId],
    queryFn: () =>
      api.get<ReimportComparison>(
        `/domains/import/compare?connectionId=${connectionId}&zoneId=${encodeURIComponent(zoneId)}`,
      ),
  });

  const records = compareQuery.data?.records ?? [];
  const quyetDinh = (rec: ComparedRecord): Decision => chon[rec.key] ?? MAC_DINH[rec.state];

  const ap = useMutation({
    mutationFn: () =>
      api.post<ApplyResult>('/domains/import/apply', {
        connectionId,
        zoneId,
        choices: records.map((rec) => ({
          key: rec.key,
          decision: quyetDinh(rec),
          expectedState: rec.state,
          expectedCfContent: rec.cf?.content ?? null,
        })),
      }),
    onSuccess: async (ket) => {
      await onDone();
      toast(
        t('domains.reimportDone')
          .replace('{c}', String(ket.createdOnCf))
          .replace('{u}', String(ket.updatedOnCf))
          .replace('{i}', String(ket.importedToDb + ket.updatedFromCf))
          .replace('{d}', String(ket.droppedFromDb)),
      );
      onClose();
    },
    onError: (err: unknown) => {
      setError(thanhApiError(err));
      // Lệch (VALIDATION_ERROR) nghĩa là bảng đã cũ — tải lại để người dùng xem
      // trạng thái mới trước khi áp lần nữa.
      if (err instanceof ApiError && err.is('VALIDATION_ERROR')) {
        void compareQuery.refetch();
      }
    },
  });

  const luaChon = (rec: ComparedRecord): { value: Decision; label: string }[] => {
    if (rec.state === 'differ') {
      return [
        { value: 'use_cf', label: t('domains.reimportUseCf') },
        { value: 'use_db', label: t('domains.reimportUseDb') },
      ];
    }
    return [
      { value: 'keep', label: t('domains.reimportKeep') },
      { value: 'drop', label: t('domains.reimportDrop') },
    ];
  };

  return (
    <Modal
      open
      wide
      title={t('domains.reimportTitle').replace('{name}', zoneName)}
      subtitle={t('domains.reimportSub')}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => ap.mutate()}
      footer={
        <>
          <span className="grow small muted">
            {t('domains.reimportCount').replace('{n}', String(records.length))}
          </span>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={records.length === 0 || ap.isPending || compareQuery.isFetching}
          >
            {ap.isPending ? t('common.processing') : t('domains.reimportApply')}
          </button>
        </>
      }
    >
      <ApiErrorAlert error={error} />
      <AsyncSection
        loading={compareQuery.isLoading}
        error={compareQuery.error}
        data={compareQuery.data}
        onRetry={() => void compareQuery.refetch()}
        skeleton={<Skeleton height={220} />}
        empty={
          <EmptyState
            icon="check"
            title={t('domains.reimportEmpty')}
            body={t('domains.reimportEmptyHint')}
          />
        }
      >
        {() =>
          records.length === 0 ? (
            <EmptyState
              icon="check"
              title={t('domains.reimportEmpty')}
              body={t('domains.reimportEmptyHint')}
            />
          ) : (
            <div className="import-list">
              <table className="tbl">
                <thead>
                  <tr>
                    <th scope="col">{t('domains.reimportRecord')}</th>
                    <th scope="col">{t('domains.reimportState')}</th>
                    <th scope="col">{t('domains.reimportDb')}</th>
                    <th scope="col">{t('domains.reimportCf')}</th>
                    <th scope="col">{t('domains.reimportChoice')}</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((rec) => (
                    <tr key={rec.key}>
                      <td>
                        <b className="mono">{rec.displayName}</b>
                        <div className="small muted">{rec.type}</div>
                      </td>
                      <td>
                        <span className={STATE_CHIP[rec.state]}>
                          {t(`domains.reimportState.${rec.state}`)}
                        </span>
                      </td>
                      <td className="small mono">{fmtSide(rec.db, t)}</td>
                      <td className="small mono">{fmtSide(rec.cf, t)}</td>
                      <td>
                        <select
                          value={quyetDinh(rec)}
                          aria-label={rec.displayName}
                          onChange={(event) =>
                            setChon((truoc) => ({
                              ...truoc,
                              [rec.key]: event.target.value as Decision,
                            }))
                          }
                        >
                          {luaChon(rec).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </AsyncSection>
    </Modal>
  );
}
